import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import {
  chunkItems,
  drawChartCard,
  drawPageChrome,
  findSvgInSource,
  getLogoDataUrl,
  placeCenteredLogo,
  svgToPngDataUrl,
} from "./exportHelpers";
import { MIN_DATE, PDF_TABLE_HEADERS } from "./constants";
import { formatLongDate, todayStr } from "./dateHelpers";

export const generateRiskAnalysisPdf = async ({
  lang,
  orgName,
  dateFrom,
  dateTo,
  timeRange,
  setNumbers,
  employees,
  includeTrainingData,
  includeReadData,
  showReportingCategory,
  isPrototypeGrouped,
  incidentChartGroups,
  defaultPool,
  trainingPool,
  readPool,
  hasRiskReportingCategoryFilter,
  riskReportingTableRecords,
  actionCards,
  riskPdfTableRecords,
  mapRecordForPdf,
  incidentExportRefs,
  incidentReportingExportRefs,
  tPerformanceExportRefs,
  incidentRef,
  tPerformanceRef,
  buildRiskPdfSummaryRows,
  buildReportingPdfSummaryRows,
  buildPerformancePdfSummaryRows,
}) => {
  const doc = new jsPDF({
    orientation: "portrait",
    unit: "pt",
    format: "a4",
  });
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const margin = 42;
  const lightRed = [252, 228, 227];
  const softRed = [189, 92, 92];
  const borderRed = [235, 196, 196];
  const bodyFill = [255, 249, 249];
  const logoDataUrl = await getLogoDataUrl();
  const downloadDate = formatLongDate(todayStr());
  const headers = PDF_TABLE_HEADERS[lang] ?? PDF_TABLE_HEADERS.en;
  const contentTop = 82;
  const footerReserve = 48;
  let y = margin;

  y = placeCenteredLogo(doc, logoDataUrl, 108, 220) + 34;
  doc.setFontSize(20);
  doc.setTextColor(88, 34, 34);
  doc.text(
    lang === "dk" ? "Risikoanalyse rapport" : "Risk Analysis Report",
    pageWidth / 2,
    y,
    {
      align: "center",
    },
  );
  y += 16;

  doc.setFontSize(11);
  doc.setTextColor(120, 64, 64);
  doc.text(orgName, pageWidth / 2, y, { align: "center" });
  y += 30;

  const allLabel = lang === "dk" ? "Alle" : "All";
  const yesLabel = lang === "dk" ? "Ja" : "Yes";
  const noLabel = lang === "dk" ? "Nej" : "No";
  const detailRows = [
    [lang === "dk" ? "Download-dato" : "Download date", downloadDate],
    [lang === "dk" ? "Organisation" : "Organization", orgName],
    [
      lang === "dk" ? "Datointerval" : "Date span",
      `${dateFrom || MIN_DATE} - ${dateTo || todayStr()}`,
    ],
    [
      lang === "dk" ? "Tidsinterval" : "Time span",
      `${timeRange[0]} - ${timeRange[1]}`,
    ],
    [
      lang === "dk" ? "Sæt" : "Sets",
      setNumbers.length ? setNumbers.join(", ") : allLabel,
    ],
    [
      lang === "dk" ? "Medarbejdere" : "Employees",
      employees.length ? employees.join(", ") : allLabel,
    ],
    [
      lang === "dk" ? "Træning med" : "Training included",
      includeTrainingData ? yesLabel : noLabel,
    ],
    [
      lang === "dk" ? "Læsning med" : "Reading included",
      includeReadData ? yesLabel : noLabel,
    ],
  ];

  if (showReportingCategory) {
    detailRows.push(
      [lang === "dk" ? "Near miss med" : "Near miss included", yesLabel],
      [lang === "dk" ? "Forebyggelse med" : "Prevention included", yesLabel],
      [
        lang === "dk"
          ? "Tvivl om rapportering med"
          : "Doubt about reporting included",
        yesLabel,
      ],
    );
  }

  doc.setFontSize(13);
  doc.setTextColor(88, 34, 34);
  doc.text(lang === "dk" ? "Rapportdetaljer" : "Report details", margin, y);

  const detailTableStartY = y + 20;
  autoTable(doc, {
    head: [
      [lang === "dk" ? "Felt" : "Field", lang === "dk" ? "Værdi" : "Value"],
    ],
    body: detailRows,
    startY: detailTableStartY,
    margin: { left: margin, right: margin },
    theme: "grid",
    styles: {
      fontSize: 10,
      cellPadding: 8,
      lineColor: borderRed,
      lineWidth: 0.6,
      overflow: "linebreak",
      valign: "middle",
    },
    headStyles: {
      fillColor: lightRed,
      textColor: softRed,
      fontStyle: "bold",
      lineColor: borderRed,
      lineWidth: 0.6,
    },
    bodyStyles: {
      fillColor: bodyFill,
      textColor: [55, 55, 55],
    },
    columnStyles: {
      0: { cellWidth: 145, fontStyle: "bold", textColor: [104, 48, 48] },
      1: { cellWidth: pageWidth - margin * 2 - 145 },
    },
  });

  const detailFinalY = doc.lastAutoTable?.finalY ?? detailTableStartY + 120;
  doc.setDrawColor(...borderRed);
  doc.setLineWidth(1);
  doc.roundedRect(
    margin,
    detailTableStartY - 12,
    pageWidth - margin * 2,
    detailFinalY - detailTableStartY + 24,
    12,
    12,
  );

  if (isPrototypeGrouped) {
    for (const group of incidentChartGroups) {
      const charts = [];
      const incidentSvg = findSvgInSource(
        incidentExportRefs.current[group.key],
      );
      const reportingSvg = findSvgInSource(
        incidentReportingExportRefs.current[group.key],
      );
      const performanceSvg = findSvgInSource(
        tPerformanceExportRefs.current[group.key],
      );

      if (incidentSvg) {
        const { dataUrl } = await svgToPngDataUrl(incidentSvg, 2200);
        charts.push({
          title: lang === "dk" ? "Faktisk hændelse" : "Real Incident",
          dataUrl,
          summaryRows: buildRiskPdfSummaryRows(
            group.defaultRecords,
            group.trainingRecords,
            group.readRecords,
          ),
        });
      }

      if (
        showReportingCategory &&
        hasRiskReportingCategoryFilter &&
        reportingSvg
      ) {
        const { dataUrl } = await svgToPngDataUrl(reportingSvg, 2200);
        charts.push({
          title: "PreVent Data",
          dataUrl,
          summaryRows: buildReportingPdfSummaryRows(
            group.reportingCategoryRecords,
          ),
        });
      }

      if (performanceSvg) {
        const { dataUrl } = await svgToPngDataUrl(performanceSvg, 2200);
        charts.push({
          title: "T-Performance",
          dataUrl,
          summaryRows: buildPerformancePdfSummaryRows(
            group.performanceRecords,
            group.actionCards,
          ),
        });
      }

      if (!charts.length) continue;

      chunkItems(charts, 2).forEach((chartChunk) => {
        doc.addPage();
        doc.setFontSize(15);
        doc.setTextColor(88, 34, 34);
        doc.text(
          group.title || group.prototype_set_id || "",
          margin,
          contentTop + 8,
        );

        const cardsTop = contentTop + 16;
        const gap = 10;
        const cardHeight =
          (pageHeight -
            cardsTop -
            footerReserve -
            gap * (chartChunk.length - 1)) /
          chartChunk.length;

        chartChunk.forEach((chart, index) => {
          drawChartCard(doc, {
            title: chart.title,
            dataUrl: chart.dataUrl,
            summaryRows: chart.summaryRows,
            x: margin,
            y: cardsTop + index * (cardHeight + gap),
            width: pageWidth - margin * 2,
            height: cardHeight,
            accentColor: softRed,
            borderColor: borderRed,
            fillColor: bodyFill,
          });
        });
      });
    }
  } else {
    const chartRefs = [
      {
        refs: [incidentExportRefs.current.default, incidentRef],
        title: lang === "dk" ? "Faktisk hændelse" : "Real Incident",
        summaryRows: buildRiskPdfSummaryRows(
          incidentChartGroups[0]?.defaultRecords ?? defaultPool,
          incidentChartGroups[0]?.trainingRecords ?? trainingPool,
          incidentChartGroups[0]?.readRecords ?? readPool,
        ),
      },
      ...(showReportingCategory && hasRiskReportingCategoryFilter
        ? [
            {
              refs: [incidentReportingExportRefs.current.default],
              title: lang === "dk" ? "PreVent Data" : "PreVent Data",
              summaryRows: buildReportingPdfSummaryRows(
                riskReportingTableRecords,
              ),
            },
          ]
        : []),
      {
        refs: [tPerformanceExportRefs.current.default, tPerformanceRef],
        title: "T-Performance",
        summaryRows: buildPerformancePdfSummaryRows(
          incidentChartGroups[0]?.performanceRecords ?? [],
          incidentChartGroups[0]?.actionCards ?? actionCards,
        ),
      },
    ];
    const chartExports = [];

    for (const { refs, title, summaryRows } of chartRefs) {
      const svgNode = refs.map((ref) => findSvgInSource(ref)).find(Boolean);
      if (!svgNode) continue;

      const { dataUrl } = await svgToPngDataUrl(svgNode, 2200);
      chartExports.push({
        title,
        dataUrl,
        summaryRows,
      });
    }

    if (chartExports.length) {
      chunkItems(chartExports, 2).forEach((chartChunk) => {
        doc.addPage();
        doc.setFontSize(15);
        doc.setTextColor(88, 34, 34);

        const cardsTop = contentTop + 12;
        const gap = 10;
        const cardHeight =
          (pageHeight -
            cardsTop -
            footerReserve -
            gap * (chartChunk.length - 1)) /
          chartChunk.length;

        chartChunk.forEach((chart, index) => {
          drawChartCard(doc, {
            title: chart.title,
            dataUrl: chart.dataUrl,
            summaryRows: chart.summaryRows,
            x: margin,
            y: cardsTop + index * (cardHeight + gap),
            width: pageWidth - margin * 2,
            height: cardHeight,
            accentColor: softRed,
            borderColor: borderRed,
            fillColor: bodyFill,
          });
        });
      });
    }
  }

  doc.addPage();
  y = contentTop;
  doc.setFontSize(15);
  doc.setTextColor(88, 34, 34);
  doc.text(lang === "dk" ? "Rå data" : "Raw records", margin, y);

  const tableRows = riskPdfTableRecords.map(mapRecordForPdf);
  if (tableRows.length) {
    const dataTableStartY = y + 18;
    const tableStartPage = doc.getNumberOfPages();
    autoTable(doc, {
      head: [headers.map((h) => h.label)],
      body: tableRows.map((row) => headers.map((h) => row[h.key])),
      startY: dataTableStartY,
      margin: {
        top: contentTop,
        right: margin,
        bottom: footerReserve,
        left: margin,
      },
      theme: "grid",
      styles: {
        fontSize: 9,
        cellPadding: 6,
        overflow: "linebreak",
        valign: "top",
        lineColor: borderRed,
        lineWidth: 0.5,
      },
      headStyles: {
        fillColor: lightRed,
        textColor: softRed,
        fontStyle: "bold",
        lineColor: borderRed,
        lineWidth: 0.5,
      },
      alternateRowStyles: { fillColor: bodyFill },
      bodyStyles: { textColor: [55, 55, 55] },
      columnStyles: {
        0: { cellWidth: 60 },
        1: { cellWidth: 48 },
        2: { cellWidth: 45 },
        3: { cellWidth: 108 },
        4: { cellWidth: 170 },
        5: { cellWidth: 80 },
      },
      tableWidth: pageWidth - margin * 2,
    });
    const finalY = doc.lastAutoTable?.finalY ?? dataTableStartY + 40;
    if (doc.getNumberOfPages() === tableStartPage) {
      doc.setDrawColor(...borderRed);
      doc.setLineWidth(1);
      doc.roundedRect(
        margin,
        dataTableStartY - 12,
        pageWidth - margin * 2,
        finalY - dataTableStartY + 24,
        12,
        12,
      );
    }
    y = finalY + 12;
  } else {
    doc.setFontSize(12);
    doc.text(
      lang === "dk" ? "Ingen rækker at eksportere." : "No rows to export.",
      margin,
      y + 24,
    );
  }

  const totalPages = doc.getNumberOfPages();
  for (let pageNumber = 1; pageNumber <= totalPages; pageNumber += 1) {
    doc.setPage(pageNumber);
    drawPageChrome(doc, logoDataUrl, pageNumber, totalPages, lang);
  }

  doc.save(`risk-analysis-${orgName || "report"}.pdf`);
};
